import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  addDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import "./HomeScreen.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Destination from "./Destination";
import ToursPackages from "./ToursPackages";
import Allservices from "./AllServices";

const LOADING_GIF = "/assets/images/globegif.gif";

// Shows the loading gif in place of an image until the real image
// has actually finished fetching, then swaps to it. Same <img> tag,
// same className/props — so it slots in wherever a normal <img> was.
const LazyImg = ({ src, alt, className = "", ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!src) return undefined;
    setLoaded(false);
    let cancelled = false;
    const preloader = new window.Image();
    preloader.src = src;
    preloader.onload = () => {
      if (!cancelled) setLoaded(true);
    };
    preloader.onerror = () => {
      if (!cancelled) setLoaded(true); // fall through so it doesn't stay stuck on gif forever
    };
    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <img
      src={loaded ? src : LOADING_GIF}
      alt={alt}
      className={`${className} ${!loaded ? "lazy-img-loading" : ""}`.trim()}
      loading="lazy"
      {...rest}
    />
  );
};

const HomeScreen = () => {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [allSearchData, setAllSearchData] = useState({ tours: [], destinations: [] });
  const searchRef = React.useRef(null);

  const TRAVELER_URLS = [
    "https://firebasestorage.googleapis.com/v0/b/roamtheworld-316bb.firebasestorage.app/o/travelers%2Fguest1.jpg?alt=media",
    "https://firebasestorage.googleapis.com/v0/b/roamtheworld-316bb.firebasestorage.app/o/travelers%2Fguest2.jpg?alt=media",
    "https://firebasestorage.googleapis.com/v0/b/roamtheworld-316bb.firebasestorage.app/o/travelers%2Fguest3.jpg?alt=media",
    "https://firebasestorage.googleapis.com/v0/b/roamtheworld-316bb.firebasestorage.app/o/travelers%2Fguest4.jpg?alt=media",
    "https://firebasestorage.googleapis.com/v0/b/roamtheworld-316bb.firebasestorage.app/o/travelers%2Fguest5.jpg?alt=media",
    "https://firebasestorage.googleapis.com/v0/b/roamtheworld-316bb.firebasestorage.app/o/travelers%2Fguest6.jpg?alt=media",
    "https://firebasestorage.googleapis.com/v0/b/roamtheworld-316bb.firebasestorage.app/o/travelers%2Fguest7.jpg?alt=media",
    "https://firebasestorage.googleapis.com/v0/b/roamtheworld-316bb.firebasestorage.app/o/travelers%2Fguest8.jpg?alt=media",
    "https://firebasestorage.googleapis.com/v0/b/roamtheworld-316bb.firebasestorage.app/o/travelers%2Fguest9.jpg?alt=media",
  ];

  const shuffleArray = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const [travelerImages] = useState(() => shuffleArray(TRAVELER_URLS));
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const [showWABtn, setShowWABtn] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const handleScroll = () => setShowWABtn(window.scrollY > 120);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchSearchData = async () => {
      try {
        const [toursSnap, destsSnap] = await Promise.all([
          getDocs(query(collection(db, "tours"), where("isActive", "==", true))),
          getDocs(query(collection(db, "destinations"), where("isActive", "==", true))),
        ]);
        const tours = toursSnap.docs.map((d) => ({ id: d.id, type: "tour", ...d.data() }));
        const destinations = destsSnap.docs.map((d) => ({ id: d.id, type: "destination", ...d.data() }));
        setAllSearchData({ tours, destinations });
      } catch (err) {
        console.error("Error fetching search data:", err);
      }
    };
    fetchSearchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchInput = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults([]); setShowResults(false); return; }
    const lower = q.toLowerCase();
    const tourResults = allSearchData.tours
      .filter((t) =>
        t.tourName?.toLowerCase().includes(lower) ||
        t.description?.toLowerCase().includes(lower)
      )
      .map((t) => ({ id: t.id, type: "tour", label: t.tourName, sub: t.description?.slice(0, 60) + "…", img: t.imageUrls?.[0] }));
    const destResults = allSearchData.destinations
      .filter((d) =>
        d.name?.toLowerCase().includes(lower) ||
        d.country?.toLowerCase().includes(lower) ||
        d.state?.toLowerCase().includes(lower) ||
        d.tagline?.toLowerCase().includes(lower)
      )
      .map((d) => ({ id: d.id, type: "destination", label: d.name, sub: [d.state, d.country].filter(Boolean).join(", "), img: d.imageUrls?.[0] }));
    setSearchResults([...destResults, ...tourResults].slice(0, 6));
    setShowResults(true);
  };

  const handleSearchResultClick = (result) => {
    setShowResults(false);
    setSearchQuery("");
    if (result.type === "destination") navigate(`/destinations/${result.id}`);
    else navigate(`/tours/${result.id}`);
  };

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const q = query(
          collection(db, "reviews"),
          orderBy("createdAt", "desc"),
          limit(4),
        );
        const snapshot = await getDocs(q);
        setReviews(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Error fetching reviews:", err);
      } finally {
        setLoadingReviews(false);
      }
    };
    fetchReviews();
  }, []);

  useEffect(() => {
    const fetchFeaturedBlogs = async () => {
      try {
        const q = query(
          collection(db, "blogs"),
          orderBy("createdAt", "desc"),
          limit(8),
        );
        const snapshot = await getDocs(q);
        const data = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((blog) => blog.isActive === true)
          .slice(0, 4);
        setFeaturedBlogs(data);
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        setLoadingBlogs(false);
      }
    };
    fetchFeaturedBlogs();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await addDoc(collection(db, "enquiry"), {
        ...form,
        enquiryType: "contact us",
        createdAt: serverTimestamp(),
      });
      setStatus("success");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error("Error submitting form: ", err);
      setStatus("error");
    }
  };

  const masonryStyle = `
    .travelers-masonry {
      columns: 4 220px;
      column-gap: 12px;
      padding: 0 1rem;
    }
    .travelers-masonry-item {
      break-inside: avoid;
      margin-bottom: 12px;
      border-radius: 16px;
      overflow: hidden;
      position: relative;
      cursor: pointer;
    }
    .travelers-masonry-item img {
      width: 100%;
      height: auto;
      display: block;
      transition: transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }
    .travelers-masonry-item:hover img {
      transform: scale(1.06);
    }
    .travelers-masonry-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 50%);
      opacity: 0;
      transition: opacity 0.35s ease;
      display: flex;
      align-items: flex-end;
      padding: 14px 16px;
    }
    .travelers-masonry-item:hover .travelers-masonry-overlay {
      opacity: 1;
    }
    .travelers-masonry-overlay span {
      color: #fff;
      font-size: 13px;
      font-weight: 500;
      letter-spacing: 0.3px;
    }
    @media (max-width: 768px) {
      .travelers-masonry {
        columns: 2 160px;
        column-gap: 8px;
        padding: 0 0.5rem;
      }
      .travelers-masonry-item {
        margin-bottom: 8px;
        border-radius: 12px;
      }
    }
    @media (max-width: 480px) {
      .travelers-masonry {
        columns: 2 130px;
      }
    }
  `;

  return (
    <div className="home-container">
      <style>{masonryStyle}</style>
      {/* Navbar Component */}
      <Navbar hideWhatsApp={true} />

      {/* Hero Section */}
      <section className="hero-section">
        <video
          className={`hero-video ${videoReady ? 'hero-video--ready' : ''}`}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onCanPlay={() => setVideoReady(true)}
        >
          <source
            src="https://firebasestorage.googleapis.com/v0/b/roamtheworld-316bb.firebasestorage.app/o/videos%2Fcan_you_generate_a_landscape_v.mp4?alt=media"
            type="video/mp4"
          />
        </video>

        <div className={`hero-video-overlay ${videoReady ? 'hero-video-overlay--ready' : ''}`}></div>

        <div className="hero-overlay">
          <h1>
            Explore The World With{" "}
            <span style={{
              background: "linear-gradient(135deg, #ff8c00, #ff5500)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent"
            }}>Confidence</span>
          </h1>
          <p>
            Discover amazing destinations, unforgettable experiences, and
            affordable travel packages.
          </p>
        </div>

        <div className="search-card" ref={searchRef}>
          <div className="search-input-wrap">
            <span className="search-icon-inner">🔍</span>
            <input
              type="text"
              className="search-main-input"
              placeholder="Search destinations, tours, packages..."
              value={searchQuery}
              onChange={handleSearchInput}
              onFocus={() => searchQuery && setShowResults(true)}
              autoComplete="off"
            />
            {searchQuery && (
              <button className="search-clear-btn" onClick={() => { setSearchQuery(""); setSearchResults([]); setShowResults(false); }}>✕</button>
            )}
          </div>
          <button className="search-submit-btn" onClick={() => searchQuery && setShowResults(true)}>
            Search
          </button>

          {showResults && (
            <div className="search-dropdown">
              {searchResults.length === 0 ? (
                <div className="search-no-results">No results for "{searchQuery}"</div>
              ) : (
                searchResults.map((r) => (
                  <div key={r.id + r.type} className="search-result-item" onClick={() => handleSearchResultClick(r)}>
                    <div className="search-result-img">
                      {r.img ? <LazyImg src={r.img} alt={r.label} /> : <span>{r.type === "destination" ? "🌍" : "🧳"}</span>}
                    </div>
                    <div className="search-result-info">
                      <span className="search-result-label">{r.label}</span>
                      <span className="search-result-sub">{r.sub}</span>
                    </div>
                    <span className={`search-result-badge ${r.type}`}>{r.type === "destination" ? "Destination" : "Tour"}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* Featured Tour Packages - DB fetch */}
      <ToursPackages previewMode={true} limit={4} />

      {/* Popular Destinations - DB fetch */}
      <Destination previewMode={true} />

      {/* Our Services - Preview 4 cards */}
      <Allservices previewMode={true} />

      {/* Customer Reviews */}
      <section className="reviews-section">
        <h2>What Our Travelers Say</h2>
        <div className="reviews-grid">
          {loadingReviews ? (
            <p
              style={{
                textAlign: "center",
                width: "100%",
                gridColumn: "1 / -1",
              }}
            >
              Loading reviews...
            </p>
          ) : reviews.length === 0 ? (
            <p
              style={{
                textAlign: "center",
                width: "100%",
                gridColumn: "1 / -1",
              }}
            >
              No reviews yet.
            </p>
          ) : (
            reviews.map((review) => (
              <div className="review-card" key={review.id}>
                {review.img && (
                  <LazyImg src={review.img} alt={review.name} />
                )}
                <h4>{review.name}</h4>
                <div className="rating">
                  {"★ ".repeat(Math.floor(Number(review.rating)))}
                </div>
                <p>"{review.text}"</p>
                {review.createdAt && (
                  <p
                    className="review-date"
                    style={{
                      fontSize: "0.85rem",
                      color: "#64748b",
                      marginTop: "0.5rem",
                    }}
                  >
                    {formatDate(review.createdAt)}
                  </p>
                )}
              </div>
            ))
          )}
        </div>

        {/* Happy Travelers Gallery */}
        <section className="happy-travelers-section">
          <div className="happy-travelers-header">
            <div>
              <p className="happy-travelers-eyebrow">
                {" "}
                Real Moments, Real Memories
              </p>
              <h2 className="happy-travelers-title">Our Happy Travelers</h2>
            </div>
            <p className="happy-travelers-subtitle">
              Every journey tells a story — here are some of our guests enjoying
              their dream destinations.
            </p>
          </div>

          <div className="travelers-masonry">
            {travelerImages.map((url, idx) => (
              <div className="travelers-masonry-item" key={idx}>
                <LazyImg
                  src={url}
                  alt={`Happy traveler ${idx + 1}`}
                />
                <div className="travelers-masonry-overlay">
                  <span>✈️ Roam The World</span>
                </div>
              </div>
            ))}
          </div>

          <div className="happy-travelers-bottom">
            <button
              className="happy-travelers-cta-btn"
              onClick={() =>
                window.open(
                  "https://wa.me/919096785868?text=Hi! I want to plan my trip like these happy travelers!",
                  "_blank",
                )
              }
            >
              <img
                src="/assets/images/whatsapp.png"
                alt="WhatsApp"
                className="whatsapp-icon-img"
              />
              Be Our Next Happy Traveler
            </button>
          </div>
        </section>
      </section>

      {/* Travel Blogs */}
      <section className="home-blogs-section">
        <div className="home-blogs-header">
          <div>
            <p className="home-blogs-eyebrow">✈️ From Our Travelers</p>
            <h2 className="home-blogs-title">Latest Travel Stories</h2>
          </div>
          <button
            className="home-blogs-viewall-btn"
            onClick={() => navigate("/blogs")}
          >
            View All Stories <i className="fas fa-arrow-right"></i>
          </button>
        </div>

        {loadingBlogs ? (
          <div className="home-blogs-loading">
            <div className="home-blogs-spinner"></div>
            <p>Loading travel stories...</p>
          </div>
        ) : featuredBlogs.length === 0 ? (
          <div className="home-blogs-empty">
            <i className="fas fa-newspaper"></i>
            <p>No blogs published yet. Check back soon!</p>
          </div>
        ) : (
          <div className="home-blogs-grid">
            {featuredBlogs.map((blog, idx) => (
              <div
                className={`home-blog-card ${idx === 0 ? "home-blog-card--featured" : ""}`}
                key={blog.id}
                onClick={() => navigate(`/blogs/${blog.id}`)}
              >
                <div className="home-blog-image">
                  {blog.coverImageUrl ? (
                    <LazyImg
                      src={blog.coverImageUrl}
                      alt={blog.title}
                    />
                  ) : (
                    <div className="home-blog-img-placeholder">
                      <i className="fas fa-image"></i>
                    </div>
                  )}
                  {blog.createdAt && (
                    <span className="home-blog-date">
                      {formatDate(blog.createdAt)}
                    </span>
                  )}
                  {idx === 0 && (
                    <span className="home-blog-featured-badge">
                      ⭐ Featured
                    </span>
                  )}
                </div>
                <div className="home-blog-content">
                  {blog.tags && blog.tags.length > 0 && (
                    <div className="home-blog-tags">
                      {blog.tags.slice(0, 2).map((tag, i) => (
                        <span className="home-blog-tag" key={i}>
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <h3 className="home-blog-title">{blog.title}</h3>
                  {blog.travellersQuote && (
                    <p className="home-blog-quote">
                      <i className="fas fa-quote-left"></i>{" "}
                      {blog.travellersQuote}
                    </p>
                  )}
                  {blog.body && (
                    <p className="home-blog-preview">
                      {blog.body.length > 100
                        ? blog.body.slice(0, 100) + "..."
                        : blog.body}
                    </p>
                  )}
                  <div className="home-blog-footer">
                    {blog.authorName ? (
                      <div className="home-blog-author">
                        <div className="home-blog-avatar">
                          {blog.authorName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <span className="home-blog-author-name">
                            {blog.authorName}
                          </span>
                          {blog.authorDesignation && (
                            <span className="home-blog-author-role">
                              {blog.authorDesignation}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span />
                    )}
                    <button
                      className="home-blog-read-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/blogs/${blog.id}`);
                      }}
                    >
                      Read More <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {featuredBlogs.length > 0 && (
          <div className="home-blogs-bottom">
            <button
              className="home-blogs-viewall-bottom-btn"
              onClick={() => navigate("/blogs")}
            >
              <i className="fas fa-newspaper"></i> Explore All Travel Stories
            </button>
          </div>
        )}
      </section>

      {/* Plan Your Dream Trip */}
      <section className="enquiry-section">
        <h2>Fill out the form and our team will get back to you Shortly.</h2>
        <p
          style={{
            textAlign: "center",
            color: "#64748b",
            marginBottom: "2rem",
            maxWidth: "600px",
            margin: "0 auto 2rem auto",
          }}
        >
          Fill out the form and our team will get back to you within 24 hours.
        </p>
        <form className="enquiry-form" onSubmit={handleSubmit}>
          <input
            type="text"
            name="name"
            placeholder="Full Name *"
            value={form.name}
            onChange={handleChange}
            required
          />
          <input
            type="tel"
            name="phone"
            placeholder="Mobile Number"
            value={form.phone}
            onChange={handleChange}
          />
          <input
            type="email"
            name="email"
            placeholder="Email Address *"
            value={form.email}
            onChange={handleChange}
            required
          />
          <input
            type="text"
            name="subject"
            placeholder="Subject (e.g., Tour enquiry, Visa help)"
            value={form.subject}
            onChange={handleChange}
          />
          <textarea
            name="message"
            placeholder="Message *"
            rows="4"
            value={form.message}
            onChange={handleChange}
            required
            style={{ borderRadius: "24px", gridColumn: "1 / -1" }}
          ></textarea>
          {status === "success" && (
            <div
              style={{
                gridColumn: "1 / -1",
                color: "#16a34a",
                fontWeight: "600",
                textAlign: "center",
                marginTop: "1rem",
              }}
            >
              ✅ Message sent! We'll get back to you shortly.
            </div>
          )}
          {status === "error" && (
            <div
              style={{
                gridColumn: "1 / -1",
                color: "#dc2626",
                fontWeight: "600",
                textAlign: "center",
                marginTop: "1rem",
              }}
            >
              ⚠️ Something went wrong. Please try WhatsApp instead.
            </div>
          )}
          <button
            type="submit"
            disabled={status === "sending"}
            style={{
              gridColumn: "1 / -1",
              opacity: status === "sending" ? 0.7 : 1,
            }}
          >
            {status === "sending" ? "Sending..." : "✉️ Send Message"}
          </button>
        </form>
      </section>

      {/* WhatsApp CTA */}
      <section className="whatsapp-section">
        <h2>Need Instant Assistance?</h2>
        <p>
          Chat with our travel experts and get personalized travel
          recommendations.
        </p>
        <button
          className="whatsapp-btn"
          onClick={() =>
            window.open(
              "https://wa.me/919096785868?text=Hi! I'd like to know more about your travel packages.",
              "_blank",
            )
          }
        >
          <img
            src="/assets/images/whatsapp.png"
            alt="WhatsApp"
            className="whatsapp-icon-img"
          />{" "}
          Chat on WhatsApp
        </button>
      </section>

      {/* Floating Call Button */}
      <a
        href="tel:+919096785868"
        className={`floating-call-btn ${showWABtn ? "floating-call-btn--visible" : ""}`}
        aria-label="Call Us"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="#ffffff"
          width="26"
          height="26"
        >
          <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
        </svg>
      </a>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/919096785868?text=Hi! I'd like to know more about your travel packages."
        target="_blank"
        rel="noopener noreferrer"
        className={`floating-wa-btn ${showWABtn ? "floating-wa-btn--visible" : ""}`}
        aria-label="Chat on WhatsApp"
      >
        <img src="/assets/images/whatsapp.png" alt="WhatsApp" />
      </a>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default HomeScreen;